import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { MessageCircle, Search, UserRound, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const formatDateTimeBR = (value) => {
  if (!value) return '';

  const raw = String(value);
  const normalized =
    raw.endsWith('Z') || raw.includes('+') || raw.includes('-03:00')
      ? raw
      : `${raw}Z`;

  return new Date(normalized).toLocaleString('pt-BR', {
    timeZone: 'America/Recife',
  });
};

const reasonLabel = (reason) => {
  if (reason === 'no_match') return 'Número não cadastrado';
  if (reason === 'multiple_users') return 'Número encontrado em mais de uma clínica';
  if (reason === 'system_user_phone') return 'Mensagem enviada por usuário do sistema';
  return 'Não vinculado';
};

const statusLabel = (status) => {
  if (status === 'assigned') return 'Encaminhada';
  if (status === 'resolved') return 'Resolvida';
  return 'Aberta';
};

const statusClass = (status) => {
  if (status === 'assigned') return 'bg-blue-100 text-blue-700';
  if (status === 'resolved') return 'bg-emerald-100 text-emerald-700';
  return 'bg-red-100 text-red-700';
};

const WhatsAppAdminPage = () => {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [search, setSearch] = useState('');
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [windowOpen, setWindowOpen] = useState(false);

  const [assignTargets, setAssignTargets] = useState([]);
  const [assignUserId, setAssignUserId] = useState('');
  const [assigning, setAssigning] = useState(false);

  const [assignPatients, setAssignPatients] = useState([]);
  const [assignPatientId, setAssignPatientId] = useState('');
  const [loadingAssignPatients, setLoadingAssignPatients] = useState(false);

  const authHeaders = {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  };

  const loadChats = async () => {
    try {
      setLoadingChats(true);
      const response = await axios.get(
        `${API_URL}/webhooks/whatsapp/admin/chats`,
        authHeaders
      );
      setChats(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar conversas WhatsApp Admin:', error);
    } finally {
      setLoadingChats(false);
    }
  };

  const loadAssignTargets = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/webhooks/whatsapp/admin/assign-targets`,
        authHeaders
      );

      setAssignTargets(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar clínicas/usuários:', error);
    }
  };

  const loadAssignPatients = async (userId) => {
    if (!userId) {
      setAssignPatients([]);
      setAssignPatientId('');
      return;
    }

    try {
      setLoadingAssignPatients(true);

      const response = await axios.get(
        `${API_URL}/webhooks/whatsapp/admin/assign-targets/${encodeURIComponent(userId)}/patients`,
        authHeaders
      );

      setAssignPatients(Array.isArray(response.data) ? response.data : []);
      setAssignPatientId('');
    } catch (error) {
      console.error('Erro ao carregar pacientes do usuário:', error);
      setAssignPatients([]);
      setAssignPatientId('');
    } finally {
      setLoadingAssignPatients(false);
    }
  };

  const loadMessages = async (chat, options = {}) => {
    try {
      if (!options.silent) {
        setSelectedChat(chat);
        setLoadingMessages(true);
      }

      const response = await axios.get(
        `${API_URL}/webhooks/whatsapp/admin/chats/${encodeURIComponent(chat.id)}/messages`,
        authHeaders
      );

      setSelectedChat(response.data?.chat || chat);
      setMessages(Array.isArray(response.data?.messages) ? response.data.messages : []);
      setWindowOpen(response.data?.window_open === true);

      await loadChats();
    } catch (error) {
      console.error('Erro ao carregar mensagens admin:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedChat?.id) return;

    const ok = window.confirm('Marcar esta conversa como resolvida?');
    if (!ok) return;

    try {
      await axios.post(
        `${API_URL}/webhooks/whatsapp/admin/chats/${encodeURIComponent(selectedChat.id)}/resolve`,
        {},
        authHeaders
      );

      await loadChats();
      setSelectedChat((prev) => (prev ? { ...prev, status: 'resolved' } : prev));
    } catch (error) {
      console.error('Erro ao resolver conversa:', error);
      alert(error.response?.data?.detail || 'Erro ao marcar conversa como resolvida.');
    }
  };

  const handleAssign = async () => {
    if (!selectedChat?.id) return;

    if (!assignUserId) {
      alert('Selecione a clínica/usuário de destino.');
      return;
    }

    const target = assignTargets.find((item) => item.id === assignUserId);

    if (!target?.whatsapp_module_active) {
      alert('Este usuário não possui o módulo WhatsApp ativo. Ative o módulo antes de encaminhar a conversa.');
      return;
    }

    const patient = assignPatients.find((item) => item.id === assignPatientId);

    const ok = window.confirm(
      `Encaminhar esta conversa para ${
        target?.full_name
          ? `${target.full_name} (${target.email})`
          : target?.email || 'o usuário selecionado'
      }${patient?.full_name ? `, vinculando ao paciente ${patient.full_name}` : ''}?`
    );

    if (!ok) return;

    try {
      setAssigning(true);

      await axios.post(
        `${API_URL}/webhooks/whatsapp/admin/chats/${encodeURIComponent(selectedChat.id)}/assign`,
        {
          user_id: assignUserId,
          patient_id: assignPatientId || null,
        },
        authHeaders
      );

      await loadChats();
      setSelectedChat((prev) =>
        prev
          ? {
              ...prev,
              status: 'assigned',
              assigned_to_user_id: assignUserId,
            }
          : prev
      );

      alert('Conversa encaminhada com sucesso.');
    } catch (error) {
      console.error('Erro ao encaminhar conversa:', error);
      alert(error.response?.data?.detail || 'Erro ao encaminhar conversa.');
    } finally {
      setAssigning(false);
    }
  };

  useEffect(() => {
    loadChats();
    loadAssignTargets();

    const interval = setInterval(() => {
      loadChats();

      if (selectedChat?.id) {
        loadMessages(selectedChat, { silent: true });
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [selectedChat?.id]);

  const filteredChats = chats.filter((chat) => {
    const term = search.toLowerCase();

    return (
      (chat.contact_name || '').toLowerCase().includes(term) ||
      (chat.phone || '').includes(term) ||
      (chat.last_message || '').toLowerCase().includes(term) ||
      reasonLabel(chat.reason).toLowerCase().includes(term)
    );
  });

  return (
    <Layout>
      <div className="h-[calc(100vh-120px)] bg-white border border-gray-200 rounded-xl overflow-hidden flex">
        <div className="w-full md:w-80 border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-red-600" />
              WhatsApp Admin
            </h2>

            <p className="text-xs text-gray-500 mt-1">
              Conversas não vinculadas a uma clínica específica.
            </p>

            <div className="mt-3 relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar conversa..."
                className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm"
              />
            </div>

            <button
              type="button"
              onClick={loadChats}
              className="mt-3 w-full inline-flex items-center justify-center px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
            >
              Atualizar conversas
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingChats ? (
              <p className="p-4 text-sm text-gray-500">Carregando conversas...</p>
            ) : filteredChats.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">Nenhuma conversa admin.</p>
            ) : (
              filteredChats.map((chat) => {
                const active = selectedChat?.id === chat.id;

                return (
                  <button
                    key={chat.id}
                    type="button"
                    onClick={() => loadMessages(chat)}
                    className={`w-full text-left p-4 border-b hover:bg-gray-50 ${
                      active ? 'bg-red-50' : 'bg-white'
                    }`}
                  >
                    <div className="flex justify-between gap-2">
                      <div className="min-w-0">
                        <div
                          className={`font-medium text-sm truncate ${
                            chat.unread_count > 0
                              ? 'text-gray-950 font-semibold'
                              : 'text-gray-900'
                          }`}
                        >
                          {chat.contact_name || 'Contato não identificado'}
                        </div>

                        {chat.phone && (
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            {chat.phone}
                          </div>
                        )}

                        <div className="text-[11px] text-red-700 mt-0.5 leading-tight">
                          {reasonLabel(chat.reason)}
                        </div>

                        {chat.reason === 'system_user_phone' &&
                          Array.isArray(chat.matched_users) &&
                          chat.matched_users.length > 0 && (
                            <div className="text-[11px] text-purple-700 mt-1 leading-tight">
                              Usuário: {chat.matched_users[0].full_name || chat.matched_users[0].email}
                            </div>
                        )}

                        <div className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusClass(chat.status)}`}>
                          {statusLabel(chat.status)}
                        </div>  

                        {chat.status === 'assigned' && (
                          <div className="text-[11px] text-blue-700 mt-1 leading-tight">
                            Encaminhada para{' '}
                            {chat.assigned_to_user_name ||
                              chat.assigned_to_user_email ||
                              'usuário selecionado'}
                            {chat.assigned_to_patient_name
                              ? ` · Paciente: ${chat.assigned_to_patient_name}`
                              : ''}
                          </div>
                        )}

                      </div>

                      {chat.unread_count > 0 && (
                        <span className="bg-red-600 text-white text-xs rounded-full px-2 py-0.5 h-fit">
                          {chat.unread_count}
                        </span>
                      )}
                    </div>

                    <div
                      className={`text-xs truncate mt-1 ${
                        chat.unread_count > 0
                          ? 'text-gray-900 font-medium'
                          : 'text-gray-500'
                      }`}
                    >
                      {chat.last_message || 'Sem mensagem'}
                    </div>

                    <div className="text-[11px] text-gray-400 mt-1">
                      {formatDateTimeBR(chat.last_message_at)}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="hidden md:flex flex-1 flex-col bg-gray-50">
          {!selectedChat ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
              <MessageCircle className="w-12 h-12 mb-3 text-gray-300" />
              <p>Selecione uma conversa admin para visualizar.</p>
            </div>
          ) : (
            <>
              <div className="p-4 border-b bg-white flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <UserRound className="w-5 h-5 text-red-700" />
                  </div>

                  <div>
                    <div className="font-semibold text-gray-900">
                      {selectedChat.contact_name || 'Contato não identificado'}
                    </div>

                    {selectedChat.phone && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {selectedChat.phone}
                      </div>
                    )}

                    <div className="text-xs text-red-700 mt-0.5">
                      {reasonLabel(selectedChat.reason)}
                    </div>

                    {selectedChat.reason === 'system_user_phone' &&
                      Array.isArray(selectedChat.matched_users) &&
                      selectedChat.matched_users.length > 0 && (
                        <div className="mt-2 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-xs text-purple-800">
                          <div className="font-semibold mb-1">
                            Mensagem enviada por usuário do sistema:
                          </div>

                          {selectedChat.matched_users.map((matchedUser) => (
                            <div key={matchedUser.id}>
                              {matchedUser.full_name || 'Usuário sem nome'} — {matchedUser.email}
                              {matchedUser.role === 'admin' ? ' · Admin' : ''}
                            </div>
                          ))}
                        </div>
                    )}

                    <div className="mt-1">
                      {windowOpen ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 text-green-700 px-2 py-0.5 text-xs">
                          🟢 Janela aberta
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-xs">
                          🟠 Janela encerrada
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2">
                    <select
                      value={assignUserId}
                      onChange={(e) => {
                        const value = e.target.value;
                        setAssignUserId(value);
                        loadAssignPatients(value);
                      }}
                      className="border border-gray-300 rounded-lg px-2 py-2 text-sm max-w-[280px]"
                      disabled={assigning || selectedChat.status === 'assigned'}
                    >
                      <option value="">Encaminhar para usuário...</option>
                      {assignTargets.map((target) => (
                        <option key={target.id} value={target.id}>
                          {target.full_name || 'Usuário sem nome'} — {target.email}
                          {target.whatsapp_module_active ? ' · WhatsApp ativo' : ' · Sem WhatsApp'}
                        </option>
                      ))}
                    </select>

                    <select
                      value={assignPatientId}
                      onChange={(e) => setAssignPatientId(e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-2 text-sm max-w-[240px]"
                      disabled={
                        assigning ||
                        selectedChat.status === 'assigned' ||
                        !assignUserId ||
                        loadingAssignPatients
                      }
                    >
                      <option value="">
                        {loadingAssignPatients
                          ? 'Carregando pacientes...'
                          : assignPatients.length > 0
                            ? 'Sem paciente específico'
                            : 'Sem pacientes'}
                      </option>

                      {assignPatients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.full_name || 'Paciente sem nome'}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      onClick={handleAssign}
                      disabled={!assignUserId || assigning || selectedChat.status === 'assigned'}
                      className="inline-flex items-center px-3 py-2 rounded-lg border border-blue-300 text-blue-700 text-sm hover:bg-blue-50 disabled:opacity-50"
                    >
                      {assigning ? 'Encaminhando...' : 'Encaminhar'}
                    </button>
                  </div>

                  {selectedChat.status === 'assigned' && (
                    <div className="text-xs text-blue-700 text-right">
                      <div>
                        Conversa encaminhada para{' '}
                        <strong>
                          {selectedChat.assigned_to_user_name ||
                            selectedChat.assigned_to_user_email ||
                            'usuário selecionado'}
                        </strong>
                      </div>

                      {selectedChat.assigned_to_patient_name && (
                        <div>
                          Paciente vinculado:{' '}
                          <strong>{selectedChat.assigned_to_patient_name}</strong>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {selectedChat.status !== 'resolved' && selectedChat.status !== 'assigned' ? (
                  <button
                    type="button"
                    onClick={handleResolve}
                    className="inline-flex items-center px-3 py-2 rounded-lg border border-emerald-300 text-emerald-700 text-sm hover:bg-emerald-50"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Marcar resolvida
                  </button>
                ) : (
                  <span className="inline-flex items-center px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Resolvida
                  </span>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {loadingMessages ? (
                  <p className="text-sm text-gray-500">Carregando mensagens...</p>
                ) : messages.length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhuma mensagem nesta conversa.</p>
                ) : (
                  messages.map((msg) => {
                    const outbound = msg.direction === 'outbound';

                    return (
                      <div
                        key={msg.id}
                        className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm shadow-sm ${
                            outbound
                              ? 'bg-red-100 text-gray-900'
                              : 'bg-white text-gray-900 border border-gray-200'
                          }`}
                        >
                          <div className="whitespace-pre-wrap text-left">
                            {msg.text || '[Mensagem sem texto]'}
                          </div>

                          <div className="text-[10px] text-gray-400 mt-1 text-right">
                            {formatDateTimeBR(msg.created_at)}

                            {outbound && (
                              <span className="ml-2">
                                {msg.read_status === 'read' || msg.delivery_status === 'read'
                                  ? 'Lida'
                                  : msg.delivery_status === 'delivered'
                                    ? 'Entregue'
                                    : msg.delivery_status === 'failed'
                                      ? 'Falhou'
                                      : 'Enviada'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="p-4 border-t bg-white">
                {selectedChat.status === 'assigned' ? (
                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                    <div className="font-medium text-blue-800">
                      Conversa encaminhada
                    </div>

                    <p className="text-sm text-blue-700 mt-2">
                      Esta conversa já foi encaminhada para a clínica selecionada. As próximas respostas devem ser feitas pela Central WhatsApp Business da clínica.
                    </p>

                    {selectedChat.assigned_to_user_name && (
                      <p className="text-sm text-blue-700 mt-2">
                        Destino: <strong>{selectedChat.assigned_to_user_name}</strong>
                        {selectedChat.assigned_to_user_email ? ` (${selectedChat.assigned_to_user_email})` : ''}
                      </p>
                    )}

                    {selectedChat.assigned_to_patient_name && (
                      <p className="text-sm text-blue-700 mt-1">
                        Paciente vinculado: <strong>{selectedChat.assigned_to_patient_name}</strong>
                      </p>
                    )}
                  </div>
                ) : windowOpen ? (
                  <>
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();

                        const text = replyText.trim();

                        if (!text || !selectedChat?.id || sendingReply) return;

                        try {
                          setSendingReply(true);

                          await axios.post(
                            `${API_URL}/webhooks/whatsapp/admin/chats/${encodeURIComponent(selectedChat.id)}/send`,
                            { text },
                            authHeaders
                          );

                          setReplyText('');
                          await loadMessages(selectedChat, { silent: true });
                          await loadChats();
                        } catch (error) {
                          console.error('Erro ao enviar resposta admin:', error);
                          alert(error.response?.data?.detail || 'Erro ao enviar mensagem.');
                        } finally {
                          setSendingReply(false);
                        }
                      }}
                      className="flex items-end gap-2"
                    >
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Digite uma resposta administrativa..."
                        rows={2}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />

                      <button
                        type="submit"
                        disabled={sendingReply || !replyText.trim()}
                        className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
                      >
                        {sendingReply ? 'Enviando...' : 'Enviar'}
                      </button>
                    </form>

                    <p className="mt-2 text-[11px] text-gray-500">
                      Use esta caixa apenas para atendimento administrativo ou identificação da clínica correta.
                    </p>
                  </>
                ) : (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <div className="font-medium text-amber-800">
                      ⏰ Janela de atendimento encerrada
                    </div>

                    <p className="text-sm text-amber-700 mt-2">
                      O WhatsApp permite respostas livres apenas durante 24 horas após a última mensagem recebida.
                    </p>

                    <button
                      type="button"
                      disabled
                      className="mt-4 px-4 py-2 rounded-lg bg-gray-300 text-gray-600 cursor-not-allowed"
                    >
                      Aguardando nova mensagem
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default WhatsAppAdminPage;